import React, { useState } from "react";

const StoreDayDistribution = () => {
  const [days, setDays] = useState({
    day0: {
      title: "Day 0",
      color: "bg-blue-50",
      stores: [
        {
          id: "1",
          name: "Store Alpha",
          city: "Mumbai",
          address: "Andheri West",
        },
        { id: "2", name: "Store Beta", city: "Mumbai", address: "Bandra" },
        {
          id: "3",
          name: "Store Gamma",
          city: "Delhi",
          address: "Connaught Place",
        },
        { id: "4", name: "Store Delta", city: "Delhi", address: "Saket" },
        {
          id: "5",
          name: "Store Epsilon",
          city: "Bangalore",
          address: "Koramangala",
        },
        {
          id: "6",
          name: "Store Zeta",
          city: "Bangalore",
          address: "Indiranagar",
        },
        { id: "7", name: "Store Theta", city: "Chennai", address: "T Nagar" },
        { id: "8", name: "Store Kappa", city: "Pune", address: "Hinjewadi" },
        {
          id: "9",
          name: "Store Lambda",
          city: "Hyderabad",
          address: "Hitech City",
        },
        {
          id: "10",
          name: "Store Omega",
          city: "Kolkata",
          address: "Park Street",
        },
      ],
    },
    day1: {
      title: "Day 1",
      color: "bg-green-50",
      stores: [],
    },
    day2: {
      title: "Day 2",
      color: "bg-purple-50",
      stores: [],
    },
    day3: {
      title: "Day 3",
      color: "bg-orange-50",
      stores: [],
    },
  });

  const [selectedStores, setSelectedStores] = useState([]);
  const [draggedStore, setDraggedStore] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null);
  const [selectedCities, setSelectedCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const allCities = [
    ...new Set(
      Object.values(days).flatMap((day) =>
        day?.stores?.map((store) => store?.city),
      ),
    ),
  ].sort();

  const handleCityToggle = (city) => {
    if (selectedCities.includes(city)) {
      setSelectedCities(selectedCities.filter((c) => c !== city));
    } else {
      setSelectedCities([...selectedCities, city]);
    }
  };

  const clearCityFilters = () => {
    setSelectedCities([]);
  };

  const handleStoreSelect = (store, dayKey) => {
    const storeWithDay = { ...store, fromDay: dayKey };
    const isSelected = selectedStores.some((s) => s.id === store.id);

    if (isSelected) {
      setSelectedStores(selectedStores.filter((s) => s.id !== store.id));
    } else {
      setSelectedStores([...selectedStores, storeWithDay]);
    }
  };

  const handleDragStart = (e, store, dayKey) => {
    setDraggedStore(store);
    setDraggedFrom(dayKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetDay) => {
    e.preventDefault();

    if (!draggedStore || !draggedFrom) return;

    const newDays = { ...days };

    newDays[draggedFrom].stores = newDays[draggedFrom].stores.filter(
      (s) => s.id !== draggedStore.id,
    );

    newDays[targetDay].stores.push(draggedStore);

    setDays(newDays);
    setDraggedStore(null);
    setDraggedFrom(null);
  };

  const moveSelectedStores = (targetDay) => {
    if (selectedStores.length === 0) return;

    const newDays = { ...days };

    selectedStores.forEach((store) => {
      newDays[store.fromDay].stores = newDays[store.fromDay].stores.filter(
        (s) => s.id !== store.id,
      );

      if (!newDays[targetDay].stores.some((s) => s.id === store.id)) {
        newDays[targetDay].stores.push(store);
      }
    });

    setDays(newDays);
    setSelectedStores([]);
  };

  const getCityColor = (city) => {
    const colors = {
      Mumbai: "bg-blue-500",
      Delhi: "bg-red-500",
      Bangalore: "bg-green-500",
      Chennai: "bg-yellow-500",
      Pune: "bg-purple-500",
      Hyderabad: "bg-pink-500",
      Kolkata: "bg-indigo-500",
    };
    return colors[city] || "bg-gray-500";
  };

  const getFilteredStores = (stores) => {
    let filtered = stores;

    // Filter by selected cities
    if (selectedCities.length > 0) {
      filtered = filtered.filter((store) =>
        selectedCities.includes(store.city),
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (store) =>
          store?.name?.toLowerCase()?.includes(query) ||
          store?.city?.toLowerCase()?.includes(query) ||
          store?.address?.toLowerCase()?.includes(query),
      );
    }

    return filtered;
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Store Distribution by Day
          </h1>

          <div className="flex items-center gap-3 flex-1 max-w-2xl">
            {/* Search Bar */}
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search stores, cities, or addresses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <svg
                className="w-4 h-4 absolute left-3 top-3 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* City Multi-Select */}
            <div className="relative">
              <button
                onClick={() => setShowCityDropdown(!showCityDropdown)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 whitespace-nowrap"
              >
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                {selectedCities.length > 0
                  ? `${selectedCities.length} Cities`
                  : "Filter Cities"}
                <svg
                  className={`w-4 h-4 transition-transform ${
                    showCityDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showCityDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowCityDropdown(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-80 overflow-y-auto">
                    <div className="p-2 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-600">
                        SELECT CITIES
                      </span>
                      {selectedCities.length > 0 && (
                        <button
                          onClick={clearCityFilters}
                          className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    {allCities.map((city) => (
                      <label
                        key={city}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCities.includes(city)}
                          onChange={() => handleCityToggle(city)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{city}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {selectedStores.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">
                  {selectedStores.length} selected
                </span>
                <button
                  onClick={() => setSelectedStores([])}
                  className="text-sm text-red-500 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {selectedStores.length > 0 && (
          <div className="mb-4 bg-white p-4 rounded-lg shadow-sm border border-blue-200">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Move selected stores to:
            </p>
            <div className="flex gap-2">
              {Object.entries(days).map(([key, day]) => (
                <button
                  key={key}
                  onClick={() => moveSelectedStores(key)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors"
                >
                  {day.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(days).map(([key, day]) => {
            const filteredStores = getFilteredStores(day.stores);
            return (
              <div
                key={key}
                className={`${day.color} rounded-lg p-4`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, key)}
              >
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-gray-800">
                      {day.title}
                    </h2>
                    <span className="bg-white text-gray-700 text-sm font-semibold px-3 py-1 rounded-full shadow-sm">
                      {day.stores.length}
                    </span>
                  </div>

                  {/* Select All Checkbox for this column */}
                  {filteredStores.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={filteredStores.every((store) =>
                          selectedStores.some((s) => s.id === store.id),
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newSelections = filteredStores
                              .filter(
                                (store) =>
                                  !selectedStores.some(
                                    (s) => s.id === store.id,
                                  ),
                              )
                              .map((store) => ({ ...store, fromDay: key }));
                            setSelectedStores([
                              ...selectedStores,
                              ...newSelections,
                            ]);
                          } else {
                            const storeIds = filteredStores.map((s) => s.id);
                            setSelectedStores(
                              selectedStores.filter(
                                (s) => !storeIds.includes(s.id),
                              ),
                            );
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-xs font-medium text-gray-600 group-hover:text-gray-800">
                        Select All
                      </span>
                    </label>
                  )}
                </div>

                <div className="space-y-2 min-h-[500px]">
                  {filteredStores.map((store) => {
                    const isSelected = selectedStores.some(
                      (s) => s.id === store.id,
                    );
                    return (
                      <div
                        key={store.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, store, key)}
                        className={`bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-all border-2 ${
                          isSelected
                            ? "border-blue-500 bg-blue-50"
                            : "border-transparent"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStoreSelect(store, key);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                            <svg
                              className="w-4 h-4 text-gray-500 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            <h3 className="text-sm font-semibold text-gray-800 cursor-move">
                              {store.name}
                            </h3>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span>{store.address}</span>
                        </div>

                        <div className="mt-2">
                          <span
                            className={`inline-block ${getCityColor(
                              store.city,
                            )} text-white text-xs font-medium px-2 py-1 rounded`}
                          >
                            {store.city}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {filteredStores.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      <svg
                        className="w-12 h-12 mx-auto mb-2 opacity-30"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <p className="text-sm">No stores</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StoreDayDistribution;
